import { useState } from 'react';
import { X, Sparkles, Wrench, ArrowUpCircle, Shield, Tag } from 'lucide-react';
import { CHANGELOG, APP_VERSION, type ChangelogEntry } from '../../data/changelog';

const TYPE_META = {
  new: { label: 'Novo', icon: Sparkles, color: 'bg-emerald-100 text-emerald-700' },
  fix: { label: 'Ispravka', icon: Wrench, color: 'bg-amber-100 text-amber-700' },
  improve: { label: 'Poboljšanje', icon: ArrowUpCircle, color: 'bg-blue-100 text-blue-700' },
  security: { label: 'Sigurnost', icon: Shield, color: 'bg-red-100 text-red-700' },
} as const;

interface Props {
  onClose: () => void;
}

export default function ChangelogModal({ onClose }: Props) {
  const [selected, setSelected] = useState<ChangelogEntry>(CHANGELOG[0]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-800 to-slate-700 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Tag className="h-5 w-5 text-white/80" />
            <div>
              <h2 className="text-white font-semibold text-base">Belkom EVA — Istorija izmena</h2>
              <p className="text-white/60 text-xs">Trenutna verzija: v{APP_VERSION}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Zatvori istoriju izmena"
            className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-44 flex-shrink-0 border-r border-gray-100 overflow-y-auto py-3 bg-gray-50">
            {CHANGELOG.map((entry) => (
              <button
                key={entry.version}
                onClick={() => setSelected(entry)}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  selected.version === entry.version
                    ? 'bg-white border-r-2 border-slate-700'
                    : 'hover:bg-white'
                }`}
              >
                <div className={`text-sm font-semibold ${selected.version === entry.version ? 'text-slate-800' : 'text-gray-600'}`}>
                  v{entry.version}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{entry.date}</div>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl font-bold text-slate-800">v{selected.version}</span>
                {selected.label && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                    {selected.label}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400 mt-0.5">{selected.date}</div>
            </div>

            <ul className="space-y-2">
              {selected.changes.map((change, index) => {
                const meta = TYPE_META[change.type];
                const Icon = meta.icon;
                return (
                  <li key={index} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 mt-0.5 ${meta.color}`}>
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed">{change.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}